import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getLocalStorage,
  removeLocalStorage,
  removeSessionItem,
  setLocalStorage,
  setSessionItem,
} from "../../util/manageStorage";
import {
  GenerateOtpRequest,
  GenerateOtpResponse,
  LoginRequest,
  PasswordLoginResponse,
  User,
  verifyOtpRequest,
  VerifyOtpResponse,
} from "../../types/authTypes";
import { toast } from "sonner";
import { ResponseError, ThunkConfig } from "../../types/responseType";
import { AppDispatch } from "../../store";
import authApis from "../../api/authApis";
import { Staff } from "../../types/staff";

// ================== Types ==================
interface UserState {
  user: User | null;
  isLoadingUser: boolean;
  isAuth: boolean;
  isAskingLogin: boolean;
  isAskingAddress: boolean;
  isNavbarVisible: boolean;
  staffList: Staff[];
}

// ================== Initial State ==================
const initialState: UserState = {
  user: getLocalStorage("user") || null,
  isLoadingUser: false,
  isAuth: false,
  isAskingLogin: false,
  isAskingAddress: false,
  isNavbarVisible: true,
  staffList: [],
};

// ================== Thunks ==================

// ✅ Password Login
export const passwordLogin = createAsyncThunk<
  PasswordLoginResponse,
  { user: LoginRequest },
  ThunkConfig
>("auth/passwordLogin", async ({ user }, thunkAPI) => {
  try {
    const response = await authApis.passwordLogin(user);

    return response?.data as PasswordLoginResponse;
  } catch (error: ResponseError | any) {
    const message = error?.message || "Login failed";
    return thunkAPI.rejectWithValue(message);
  }
});

// ✅ Generate OTP
export const generateOtp = createAsyncThunk<
  GenerateOtpResponse,
  GenerateOtpRequest,
  ThunkConfig
>("auth/generateOtp", async ({ phoneNo }, thunkAPI) => {
  try {
    const response = await authApis.generateOtp({ phoneNo });

    return response?.data as GenerateOtpResponse;
  } catch (error: ResponseError | any) {
    const message = error?.message || "Error while sending OTP";
    return thunkAPI.rejectWithValue(message);
  }
});

// ✅ Verify OTP
export const verifyOtp = createAsyncThunk<
  VerifyOtpResponse,
  verifyOtpRequest,
  { rejectValue: string; dispatch: AppDispatch }
>("auth/verifyOtp", async (request, thunkAPI) => {
  try {
    const response = await authApis.verifyOtp(request);

    const user = response?.data;

    return response?.data as VerifyOtpResponse;
  } catch (error: ResponseError | any) {
    const message =
      error?.status == 401
        ? "Invalid OTP"
        : error?.response?.data?.error?.message || "Invalid OTP";
    return thunkAPI.rejectWithValue(message);
  }
});

export const getStaffByEmpCode = createAsyncThunk<
  Staff[],
  { empCode: string },
  ThunkConfig
>("auth/getStaffByEmpCode", async ({ empCode }, thunkAPI) => {
  try {
    const response = await authApis.getStaffByEmpCode(empCode);
    return response?.data as Staff[];
  } catch (error: ResponseError | any) {
    const message =
      error?.response?.data?.error?.message || "Failed to fetch staff";
    return thunkAPI.rejectWithValue(message);
  }
});

export const staffPasswordLogin = createAsyncThunk<
  PasswordLoginResponse,
  { staffId: number; password: string },
  ThunkConfig
>("auth/staffPasswordLogin", async ({ staffId, password }, thunkAPI) => {
  try {
    const response = await authApis.staffLogin({ staffId, password });
    return response?.data as PasswordLoginResponse;
  } catch (error: ResponseError | any) {
    const message = error?.response?.data?.error?.message || "Login failed";
    return thunkAPI.rejectWithValue(message);
  }
});

// ================== Slice ==================
const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    logout: (state, action: PayloadAction<string | null | undefined>) => {
      state.user = null;
      const message = action.payload ?? "Logout Successful";
      toast.dismiss();
      if (action?.payload) toast.warning(message);

      removeSessionItem("user");
      removeLocalStorage("user");
    },
    setUser: (
      state,
      action: PayloadAction<{ isAlways: boolean; user: User }>,
    ) => {
      state.user = action.payload.user;
      if (action.payload.isAlways) {
        setLocalStorage("user", action.payload.user);
      } else {
        setSessionItem("user", action.payload.user);
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoadingUser = action.payload;
    },
    setAuth: (state, action: PayloadAction<boolean>) => {
      state.isAuth = action.payload;
    },
    setAskingLogin: (state, action: PayloadAction<boolean>) => {
      state.isAskingLogin = action.payload;
    },
    setAskingAddress: (state, action: PayloadAction<boolean>) => {
      state.isAskingAddress = action.payload;
    },
    setNavbarVisible: (state, action: PayloadAction<boolean>) => {
      state.isNavbarVisible = action.payload;
    },
  },

  // ================== Extra Reducers ==================
  extraReducers: (builder) => {
    builder
      // ----- Password Login -----
      .addCase(passwordLogin.pending, (state) => {
        state.isLoadingUser = true;
      })
      .addCase(passwordLogin.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isLoadingUser = false;
        state.isAskingLogin = false;
        toast.dismiss();
        toast.success("Login Successful");
        setLocalStorage("user", action.payload);
        //  setSessionItem("user", action.payload);
      })
      .addCase(passwordLogin.rejected, (state, { payload }) => {
        toast.error(payload as string);
        state.isLoadingUser = false;
      })

      // ----- Generate OTP -----
      .addCase(generateOtp.pending, (state) => {
        state.isLoadingUser = true;
      })
      .addCase(generateOtp.fulfilled, (state) => {
        state.isLoadingUser = false;
        toast.success("OTP sent successfully!");
      })
      .addCase(generateOtp.rejected, (state, { payload }) => {
        toast.error(payload as string);
        state.isLoadingUser = false;
      })

      // ----- Verify OTP -----
      .addCase(verifyOtp.pending, (state) => {
        state.isLoadingUser = true;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.user = action.payload.data;
        state.isLoadingUser = false;
        state.isAskingLogin = false;
        toast.dismiss();
        toast.success("Login Successful");
        // setSessionItem("user", action.payload.data);
        setLocalStorage("user", action.payload);
      })
      .addCase(verifyOtp.rejected, (state, { payload }) => {
        toast.error(payload as string);
        state.isLoadingUser = false;
      })
      .addCase(staffPasswordLogin.pending, (state) => {
        state.isLoadingUser = true;
      })
      .addCase(staffPasswordLogin.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isLoadingUser = false;
        toast.dismiss();
        toast.success("Login Successful");
        // setSessionItem("user", action.payload);
        setLocalStorage("user", action.payload);
      })
      .addCase(staffPasswordLogin.rejected, (state, { payload }) => {
        toast.error(payload as string);
        state.isLoadingUser = false;
      });
  },
});

// ================== Exports ==================
export const {
  logout,
  setUser,
  setLoading,
  setAuth,
  setAskingLogin,
  setAskingAddress,
  setNavbarVisible,
} = userSlice.actions;

export default userSlice.reducer;
